import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "beneficiariesInput", "agregadosInput",
    "decrementBeneficiaries", "incrementBeneficiaries",
    "decrementAgregados", "incrementAgregados",
    "beneficiariesCharge", "agregadosCharge",
    "beneficiariesMax", "agregadosMax",
    "beneficiariesSubtitle", "agregadosSubtitle"
  ]

  static values = {
    maxBeneficiaries: { type: Number, default: 0 },
    maxAgregados: { type: Number, default: 0 },
    includedBeneficiaries: { type: Number, default: 0 },
    includedAgregados: { type: Number, default: 0 },
    beneficiaryPrice: { type: Number, default: 0 },
    agregadoPrice: { type: Number, default: 0 }
  }

  connect() {
    this.updateUI()
  }

  updateUI() {
    this.clampBeneficiaries()
    this.clampAgregados()
    this.updateButtonStates()
    this.updateChargeDisplays()
    this.updateMaxLabels()
    this.updateSubtitles()
    this.updateInputMax()
  }

  incrementBeneficiaries(event) {
    event.preventDefault()
    const current = this.getBeneficiariesCount()
    if (current < this.maxBeneficiariesValue) {
      this.setBeneficiariesCount(current + 1)
    }
  }

  decrementBeneficiaries(event) {
    event.preventDefault()
    const current = this.getBeneficiariesCount()
    if (current > 0) {
      this.setBeneficiariesCount(current - 1)
    }
  }

  incrementAgregados(event) {
    event.preventDefault()
    const current = this.getAgregadosCount()
    if (current < this.maxAgregadosValue) {
      this.setAgregadosCount(current + 1)
    }
  }

  decrementAgregados(event) {
    event.preventDefault()
    const current = this.getAgregadosCount()
    if (current > 0) {
      this.setAgregadosCount(current - 1)
    }
  }

  updateBeneficiaries() {
    this.clampBeneficiaries()
    this.updateButtonStates()
    this.updateChargeDisplays()
    this.dispatchChangeEvent()
  }

  updateAgregados() {
    this.clampAgregados()
    this.updateButtonStates()
    this.updateChargeDisplays()
    this.dispatchChangeEvent()
  }

  getBeneficiariesCount() {
    return parseInt(this.beneficiariesInputTarget.value) || 0
  }

  getAgregadosCount() {
    return parseInt(this.agregadosInputTarget.value) || 0
  }

  setBeneficiariesCount(value) {
    this.beneficiariesInputTarget.value = value
    this.updateButtonStates()
    this.updateChargeDisplays()
    this.dispatchChangeEvent()
    // Trigger input event for other controllers listening
    this.beneficiariesInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  setAgregadosCount(value) {
    this.agregadosInputTarget.value = value
    this.updateButtonStates()
    this.updateChargeDisplays()
    this.dispatchChangeEvent()
    // Trigger input event for other controllers listening
    this.agregadosInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  clampBeneficiaries() {
    let value = this.getBeneficiariesCount()
    value = Math.max(0, Math.min(value, this.maxBeneficiariesValue))
    this.beneficiariesInputTarget.value = value
  }

  clampAgregados() {
    let value = this.getAgregadosCount()
    value = Math.max(0, Math.min(value, this.maxAgregadosValue))
    this.agregadosInputTarget.value = value
  }

  updateButtonStates() {
    const beneficiaries = this.getBeneficiariesCount()
    const agregados = this.getAgregadosCount()

    if (this.hasDecrementBeneficiariesTarget) {
      this.decrementBeneficiariesTarget.disabled = beneficiaries <= 0
    }
    if (this.hasIncrementBeneficiariesTarget) {
      this.incrementBeneficiariesTarget.disabled = beneficiaries >= this.maxBeneficiariesValue
    }
    if (this.hasDecrementAgregadosTarget) {
      this.decrementAgregadosTarget.disabled = agregados <= 0
    }
    if (this.hasIncrementAgregadosTarget) {
      this.incrementAgregadosTarget.disabled = agregados >= this.maxAgregadosValue
    }
  }

  updateChargeDisplays() {
    const beneficiaries = this.getBeneficiariesCount()
    const agregados = this.getAgregadosCount()

    const chargeableBeneficiaries = Math.max(0, beneficiaries - this.includedBeneficiariesValue)
    const chargeableAgregados = Math.max(0, agregados - this.includedAgregadosValue)

    if (this.hasBeneficiariesChargeTarget) {
      if (chargeableBeneficiaries > 0) {
        const charge = chargeableBeneficiaries * this.beneficiaryPriceValue
        this.beneficiariesChargeTarget.textContent = `+${this.formatCurrency(charge)}/mês`
      } else {
        this.beneficiariesChargeTarget.textContent = "Sem taxa extra"
      }
    }

    if (this.hasAgregadosChargeTarget) {
      if (chargeableAgregados > 0) {
        const charge = chargeableAgregados * this.agregadoPriceValue
        this.agregadosChargeTarget.textContent = `+${this.formatCurrency(charge)}/mês`
      } else {
        this.agregadosChargeTarget.textContent = "Sem taxa extra"
      }
    }
  }

  updateMaxLabels() {
    if (this.hasBeneficiariesMaxTarget) {
      this.beneficiariesMaxTarget.textContent = `Máximo: ${this.maxBeneficiariesValue}`
    }
    if (this.hasAgregadosMaxTarget) {
      this.agregadosMaxTarget.textContent = `Máximo: ${this.maxAgregadosValue}`
    }
  }

  updateSubtitles() {
    if (this.hasBeneficiariesSubtitleTarget) {
      this.beneficiariesSubtitleTarget.innerHTML = this.buildSubtitle(
        this.includedBeneficiariesValue,
        this.beneficiaryPriceValue,
        "beneficiário"
      )
    }
    if (this.hasAgregadosSubtitleTarget) {
      this.agregadosSubtitleTarget.innerHTML = this.buildSubtitle(
        this.includedAgregadosValue,
        this.agregadoPriceValue,
        "agregado"
      )
    }
  }

  buildSubtitle(included, price, type) {
    if (included > 0 && price > 0) {
      return `<span class="text-green-600 font-medium">${included} incluso(s) na mensalidade</span> • Excedente: ${this.formatCurrency(price)}/cada`
    } else if (included > 0) {
      return `<span class="text-green-600 font-medium">${included} incluso(s) na mensalidade</span>`
    } else if (price > 0) {
      return `Valor por ${type}: ${this.formatCurrency(price)}/mês`
    }
    return `<span class="text-green-600 font-medium">Incluso na mensalidade</span>`
  }

  updateInputMax() {
    if (this.hasBeneficiariesInputTarget) {
      this.beneficiariesInputTarget.max = this.maxBeneficiariesValue
    }
    if (this.hasAgregadosInputTarget) {
      this.agregadosInputTarget.max = this.maxAgregadosValue
    }
  }

  formatCurrency(value) {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    })
  }

  dispatchChangeEvent() {
    // Dispatch custom event for other controllers to listen to
    this.element.dispatchEvent(new CustomEvent("beneficiaries-changed", {
      bubbles: true,
      detail: {
        beneficiaries: this.getBeneficiariesCount(),
        agregados: this.getAgregadosCount()
      }
    }))
  }
}
